/**
 * Calls API Service
 * CRUD over the org-scoped `/api/calls` endpoints. Multi-tenancy is enforced
 * server-side from the organization_id claim on the Firebase token that the
 * shared fetch client attaches to every request.
 */

import { get, post, patch, del, getBlob } from "./api";
import type {
  PaginatedResponse,
  CallCalendarItem,
  CallResponse,
  CallStatus,
  CreateCallRequest,
  UpdateCallRequest,
  BatchCreateCallsRequest,
  BatchCreateCallsResponse,
} from "../types/api";
import type { CalendarScope } from "./calendar";

export type GetCallsParams = {
  prospectId?: string;
  coachId?: string;
  status?: CallStatus;
  pageNumber?: number;
  pageSize?: number;
};

/**
 * Fetch a paginated list of calls, optionally filtered by prospect, assigned
 * coach, and/or status.
 */
export const getCalls = async (
  params: GetCallsParams = {}
): Promise<PaginatedResponse<CallResponse>> => {
  const { prospectId, coachId, status, pageNumber = 1, pageSize = 20 } = params;

  const queryParams = new URLSearchParams({
    pageNumber: String(pageNumber),
    pageSize: String(pageSize),
  });

  if (prospectId) {
    queryParams.append("prospectId", prospectId);
  }

  if (coachId) {
    queryParams.append("coachId", coachId);
  }

  if (status) {
    queryParams.append("status", status);
  }

  return get<PaginatedResponse<CallResponse>>(
    `/api/calls?${queryParams.toString()}`
  );
};

/**
 * Fetch the scheduled calls overlapping [fromIso, toIso) for the schedule view. Both bounds
 * are ISO-8601 UTC instants; pass the same window the calendar-events read uses so the two
 * halves of the grid agree.
 *
 * Calls with no time are never returned — they stay on the call list. Pass scope="mine" to
 * limit to the current user's own calls, matching the calendar's "My events" toggle.
 */
export const getCallCalendarItems = async (
  fromIso: string,
  toIso: string,
  scope: CalendarScope = "all",
): Promise<CallCalendarItem[]> => {
  const params = new URLSearchParams({ from: fromIso, to: toIso });
  if (scope === "mine") {
    params.set("scope", "mine");
  }
  return get<CallCalendarItem[]>(`/api/calls/calendar?${params.toString()}`);
};

/** Fetch a single call by id (404 if it belongs to another org). */
export const getCall = async (id: string): Promise<CallResponse> => {
  return get<CallResponse>(`/api/calls/${id}`);
};

/** Schedule a new call; returns the persisted call. */
export const createCall = async (
  data: CreateCallRequest
): Promise<CallResponse> => {
  return post<CallResponse>("/api/calls", data);
};

/**
 * Partially update a call. Powers reschedule, reassign, mark-complete/reopen,
 * and note edits via the same endpoint.
 */
export const updateCall = async (
  id: string,
  data: UpdateCallRequest
): Promise<CallResponse> => {
  return patch<CallResponse>(`/api/calls/${id}`, data);
};

/** Delete a call. */
export const deleteCall = async (id: string): Promise<void> => {
  return del<void>(`/api/calls/${id}`);
};

/**
 * Create a whole call list in one request.
 *
 * Resolves even when some assignments fail: the endpoint always returns 200 and reports
 * outcomes per assignment, in request order, so the caller renders failures against the
 * rows they belong to rather than discarding a mostly-successful batch. Only a malformed
 * request (no assignments, no scheduledAt) or an auth failure rejects.
 */
export const batchCreateCalls = async (
  data: BatchCreateCallsRequest
): Promise<BatchCreateCallsResponse> => {
  return post<BatchCreateCallsResponse>("/api/calls/batch", data);
};

export type ExportCallsParams = {
  /** Inclusive first calendar day, `YYYY-MM-DD`. */
  startDate: string;
  /** Inclusive last calendar day, `YYYY-MM-DD`. */
  endDate: string;
  coachId?: string;
  status?: CallStatus;
};

/**
 * Name for the downloaded workbook. Owned here rather than parsed out of Content-Disposition,
 * which `getBlob` does not surface — the range is the only thing in it, and the client already
 * knows the range.
 */
export const callExportFilename = (startDate: string, endDate: string): string =>
  `call-log_${startDate}_to_${endDate}.xlsx`;

/**
 * Download the call log for a date range as an Excel workbook — the compliance report
 * coaches file with Arms. Returns the raw blob so the caller decides when to save it.
 */
export const exportCalls = async (params: ExportCallsParams): Promise<Blob> => {
  const { startDate, endDate, coachId, status } = params;

  const queryParams = new URLSearchParams({
    startDate,
    endDate,
    // The days a coach is reporting on are their days: a 7:00 PM Central call on Aug 31 is
    // stored as Sep 1 UTC and has to stay inside an August report, so the API resolves the
    // range against this zone. resolvedOptions().timeZone is an IANA id in every browser the
    // app supports; `||` rather than `??` because a locked-down environment reports an empty
    // string here, not undefined, and the API would then read the range in UTC without saying so.
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
  });

  if (coachId) {
    queryParams.append("coachId", coachId);
  }

  if (status) {
    queryParams.append("status", status);
  }

  return await getBlob(`/api/calls/export?${queryParams.toString()}`);
};
